import type { RoomOccupancy, RoomsParticipantDistribution } from '@/domain/shared/types'
import type { SearchState } from '@/scopes/search/search/infrastructure/store/store'
import { CHILDREN_MAX_AGE, CHILDREN_MIN_AGE } from '@/domain/shared/constants'
import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import { DEFAULT_RESULTS_PER_PAGE } from '@/scopes/search/search/domain/constants'
import { getPassThroughParams } from '@/scopes/search/search/domain/getPassThroughParams'
import {
  FiltersSelection,
  SearchFilters,
  SearchFilterSelection,
  SearchFilterType,
} from '@/scopes/search/search/infrastructure/api/types'
import { searchPassThroughParams, type SearchPassThroughParams } from '@/scopes/search/types'
import { sub } from 'date-fns'
import { always, isEmpty } from 'ramda'

export interface DeserializedSearchQueryString {
  limit?: string
  offset?: string
  sort?: string
  Lastminute?: string
  Stars?: string
  MetersToBeach?: string
  MetersToCenter?: string
  RoomCount?: string
  TransportType?: string | string[]
  AccommodationType?: string[]
  ArrivalAirport?: string[]
  Campaigns?: string[]
  ChildrenFacilities?: string[]
  City?: string[]
  Country?: string[]
  Collection?: string[]
  DepartureDate?: string[]
  DepartureAirport?: string[]
  Discount?: string[]
  Duration?: string[]
  Facilities?: string[]
  InternetFacilities?: string[]
  Mealplan?: string[]
  Region?: string[]
  Themes?: string[]
  PricePerPerson?: string[]
  CustomersRating?: string
  Participants?: string[][]
  CampaignsAndDiscounts?: string[]
}

export type DeserializedQueryStringToStateMapper = (
  deserializedQueryString: DeserializedSearchQueryString,
  passThroughParams?: Partial<SearchPassThroughParams>
) => Partial<SearchState>

type LowercaseKeys<T> = {
  [K in keyof T as K extends string ? Lowercase<K> : K]: T[K];
}

interface Dependencies {
  queryStringSerializer: QueryStringSerializer
  mappers: DeserializedQueryStringToStateMapper[]
  defaultValues: Partial<FiltersSelection<SearchFilters>>
}

function lowercaseKeys(obj: Record<string, string | string[]>): Record<string, string | string[]> {
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key]
    if (Array.isArray(value)) {
      const currentAccKeyValue = (acc[key.toLowerCase()] as string[]) || []
      const filteredNewValue = value.filter((v: string) => v !== '')
      return { ...acc, [key.toLowerCase()]: [...currentAccKeyValue, ...filteredNewValue] }
    }
    return { ...acc, [key.toLowerCase()]: obj[key] }
  }, {} as Record<string, string | string[]>)
}

export function paginationMapper({ offset, limit }: LowercaseKeys<DeserializedSearchQueryString>): Partial<SearchState> {
  if (!limit && !offset)
    return {}

  const parsedOffset = offset ? Number.parseInt(offset, 10) : 0
  const parsedLimit = limit ? Number.parseInt(limit, 10) : DEFAULT_RESULTS_PER_PAGE

  return {
    pagination: {
      offset: parsedOffset,
      limit: parsedLimit,
    },
  }
}

export function sortingMapper({ sort }: LowercaseKeys<DeserializedSearchQueryString>): Partial<SearchState> {
  const sortOptions = ['CustomersRating', 'Price', 'Popularity', 'DepartureDate']
  const sortValue = sortOptions.find((option: string) => option.toLowerCase() === sort?.toLowerCase())
  if (!sort || !sortValue)
    return {}

  return {
    sorting: {
      by: sortValue,
    },
  }
}

export function flightOnlyMapper(_queryString: LowercaseKeys<DeserializedSearchQueryString>, { isFlightOnly }: Partial<SearchPassThroughParams>): Partial<SearchState> {
  return {
    isFlightOnly: isFlightOnly === 'true',
  }
}

export function combineFilterSelectionMappers(...mappers: DeserializedQueryStringToStateMapper[]): DeserializedQueryStringToStateMapper {
  if (isEmpty(mappers))
    return always({})

  return (deserializedQueryString, passThroughParams) => ({
    filterSelection: mappers.reduce<Partial<SearchFilterSelection>>(
      (acc, mapper) => ({
        ...acc,
        ...mapper(deserializedQueryString, passThroughParams).filterSelection,
      }),
      {},
    ),
  })
}

export function identityFilterSelectionArrayMapper<K extends SearchFilterType>(filterType: K, defaultValue?: unknown): DeserializedQueryStringToStateMapper {
  return (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
    const lowerCasedFilterType = filterType.toLowerCase() as keyof LowercaseKeys<DeserializedSearchQueryString>
    const filterSelectedValue = deserializedQueryString?.[lowerCasedFilterType] ?? defaultValue
    if (filterSelectedValue === undefined)
      return { filterSelection: {} }
    const filterSelectedValueUppercased = (filterSelectedValue as string[]).map((option: string) => option.toUpperCase())
    return {
      filterSelection: {
        [filterType]: filterSelectedValueUppercased,
      },
    }
  }
}

export function identityFilterSelectionMapper<K extends SearchFilterType>(filterType: K, defaultValue?: unknown): DeserializedQueryStringToStateMapper {
  return (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
    const lowerCasedFilterType = filterType.toLowerCase() as keyof LowercaseKeys<DeserializedSearchQueryString>
    const filterSelectedValue = deserializedQueryString?.[lowerCasedFilterType] ?? defaultValue
    if (filterSelectedValue === undefined)
      return { filterSelection: {} }
    return {
      filterSelection: {
        [filterType]: filterSelectedValue,
      },
    }
  }
}

export const lastMinuteMapper: DeserializedQueryStringToStateMapper = (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
  if (!deserializedQueryString?.lastminute)
    return { filterSelection: {} }
  const { lastminute } = deserializedQueryString

  return {
    filterSelection: {
      Lastminute: lastminute?.toLowerCase(),
    },
  }
}

export const collectionMapper: DeserializedQueryStringToStateMapper = (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
  if (!deserializedQueryString?.collection)
    return { filterSelection: {} }
  const { collection } = deserializedQueryString
  const collectionValue = collection.map((value: string) => value.toLowerCase())

  return {
    filterSelection: {
      Collection: collectionValue,
    },
  }
}

export const pricePerPersonMapper: DeserializedQueryStringToStateMapper = (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
  if (!deserializedQueryString?.priceperperson)
    return { filterSelection: {} }
  const { priceperperson } = deserializedQueryString

  if (priceperperson[1] === '999999') {
    return {
      filterSelection: {
        PricePerPerson: [Number.parseInt(priceperperson[0], 10), null],
      },
    }
  }

  return {
    filterSelection: {
      PricePerPerson: [Number.parseInt(priceperperson[0], 10), Number.parseInt(priceperperson[1], 10)],
    },
  }
}

export const departureDateMapper: DeserializedQueryStringToStateMapper = (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
  if (!deserializedQueryString?.departuredate)
    return { filterSelection: {} }
  const { departuredate } = deserializedQueryString
  const value = departuredate.length === 1 ? departuredate[0] : departuredate

  return {
    filterSelection: {
      DepartureDate: value,
    },
  }
}

export function transportTypeMapper(defaultValue: string): DeserializedQueryStringToStateMapper {
  return (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
    if (!deserializedQueryString?.transporttype) {
      return {
        filterSelection: {
          TransportType: defaultValue,
        },
      }
    }
    const { transporttype } = deserializedQueryString
    const value = transporttype.length === 1 ? transporttype[0] : transporttype
    const transportTypeOptions = ['Flight', 'Bus', 'SelfDrive']
    const selectedTransportType = transportTypeOptions.find((option: string) => option.toLowerCase() === (value as string).toLowerCase())

    return {
      filterSelection: {
        TransportType: selectedTransportType || defaultValue,
      },
    }
  }
}

export function durationMapper(defaultValue: string[] | undefined): DeserializedQueryStringToStateMapper {
  return (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>, passThroughParams?: Partial<SearchPassThroughParams>) => {
    const filterSelectedValue
      = deserializedQueryString?.duration ?? (passThroughParams?.autoLoad === 'false' ? undefined : defaultValue || [])
    if (filterSelectedValue === undefined)
      return { filterSelection: {} }

    return {
      filterSelection: {
        Duration: filterSelectedValue,
      },
    }
  }
}

export function participantsMapper(defaultValue: RoomsParticipantDistribution | undefined): DeserializedQueryStringToStateMapper {
  return (deserializedQueryString: DeserializedSearchQueryString) => {
    if (!deserializedQueryString?.Participants) {
      return {
        filterSelection: {
          Participants: defaultValue || [
            {
              adults: 2,
              children: [],
              babies: [],
            },
          ],
        },
      }
    }
    const { Participants } = deserializedQueryString

    const adultAgeThreshold = sub(new Date(), { years: CHILDREN_MAX_AGE, days: 1 })
    const childrenAgeThreshold = sub(new Date(), { years: CHILDREN_MIN_AGE, days: 1 })

    const mappedParticipants: RoomsParticipantDistribution = []

    Participants.forEach((room) => {
      const result: RoomOccupancy = {
        adults: 0,
        children: [],
        babies: [],
      }

      room.forEach((participant) => {
        const birthDate = new Date(participant)

        if (birthDate < adultAgeThreshold) {
          result.adults += 1
        }
        else if (birthDate < childrenAgeThreshold) {
          result.children.push(birthDate)
        }
        else {
          result.babies.push(birthDate)
        }
      })

      mappedParticipants.push(result)
    })

    return {
      filterSelection: {
        Participants: mappedParticipants,
      },
    }
  }
}

export function createQueryStringToSearchStateMapperIgnoreCasing(dependencies: Partial<Dependencies> = {}) {
  const { defaultValues } = dependencies

  const {
    mappers = [
      paginationMapper,
      sortingMapper,
      flightOnlyMapper,
      combineFilterSelectionMappers(
        identityFilterSelectionArrayMapper('AccommodationType'),
        identityFilterSelectionArrayMapper('ChildrenFacilities'),
        identityFilterSelectionArrayMapper('DepartureAirport'),
        identityFilterSelectionArrayMapper('Facilities'),
        identityFilterSelectionArrayMapper('InternetFacilities'),
        identityFilterSelectionArrayMapper('Mealplan'),
        identityFilterSelectionArrayMapper('Themes'),
        identityFilterSelectionArrayMapper('CampaignsAndDiscounts'),
        identityFilterSelectionMapper('Campaigns'),
        identityFilterSelectionMapper('Discount'),
        identityFilterSelectionMapper('ArrivalAirport'),
        identityFilterSelectionMapper('City'),
        identityFilterSelectionMapper('Country'),
        identityFilterSelectionMapper('CustomersRating'),
        identityFilterSelectionMapper('MetersToBeach'),
        identityFilterSelectionMapper('MetersToCenter'),
        identityFilterSelectionMapper('Region'),
        identityFilterSelectionMapper('RoomCount'),
        identityFilterSelectionMapper('Stars'),
        identityFilterSelectionMapper('HasAvailability'),
        identityFilterSelectionMapper('HasAvailabilityNextSeason'),
        durationMapper(defaultValues?.Duration as string[] | undefined),
        transportTypeMapper(defaultValues?.TransportType as string | 'Flight'),
        lastMinuteMapper,
        collectionMapper,
        departureDateMapper,
        pricePerPersonMapper,
        participantsMapper(defaultValues?.Participants as RoomsParticipantDistribution | undefined),
      ),
    ],
    queryStringSerializer = createQueryStringSerializer(),
  } = dependencies

  function map(queryString: string): Partial<SearchState> {
    const deserializedQueryString = queryStringSerializer.deserialize(queryString)
    const passThroughParams = getPassThroughParams(new URLSearchParams(queryString), searchPassThroughParams)
    const deserializedQueryStringLowercased = lowercaseKeys((deserializedQueryString as Record<string, string | string[]>))
    return mappers.reduce(
      (acc, mapper) => ({
        ...acc,
        ...mapper(deserializedQueryStringLowercased, passThroughParams),
      }),
      {},
    )
  }

  return { map }
}
